'use client'

import { useEffect, useMemo, useState } from 'react'

import { useAuth } from '@/contexts/AuthContext'
import { useWorkspace } from '@/contexts/WorkspaceContext'
import { fetchWithAuth } from '@/utils/api-error-handler'

type TeamDetailResponse = {
  id: string
  role?: string
}

type TeamsListResponse = {
  data?: Array<{
    id: string
    role?: string
  }>
}

const ownerRoleCache = new Map<string, boolean>()

export function useActiveTeamOwner() {
  const { user } = useAuth()
  const { activeTeamId } = useWorkspace()
  const hasSession = Boolean(user)
  const [isOwner, setIsOwner] = useState<boolean>(!activeTeamId || !hasSession)
  const [isLoading, setIsLoading] = useState<boolean>(Boolean(activeTeamId && hasSession))

  useEffect(() => {
    if (!hasSession) {
      setIsOwner(true)
      setIsLoading(false)
      return
    }
    if (!activeTeamId) {
      setIsOwner(true)
      setIsLoading(false)
      return
    }

    const cached = ownerRoleCache.get(activeTeamId)
    if (typeof cached === 'boolean') {
      setIsOwner(cached)
      setIsLoading(false)
      return
    }

    let cancelled = false
    setIsLoading(true)

    const load = async () => {
      try {
        const apiUrl = process.env.NEXT_PUBLIC_API_URL
        const teamsUrl = apiUrl ? `${apiUrl}/v1/teams` : '/v1/teams'
        const teamsResponse = await fetchWithAuth(
          teamsUrl,
          { method: 'GET', headers: { Accept: 'application/json' } },
          undefined,
          { skipUnauthorizedRedirect: true }
        )
        if (teamsResponse.ok) {
          const teamsPayload = (await teamsResponse.json()) as TeamsListResponse
          const team = Array.isArray(teamsPayload?.data)
            ? teamsPayload.data.find((item) => String(item?.id || '') === String(activeTeamId))
            : undefined
          if (team) {
            const owner = String(team.role || '').trim().toLowerCase() === 'owner'
            ownerRoleCache.set(activeTeamId, owner)
            if (!cancelled) setIsOwner(owner)
            return
          }
        }

        const teamUrl = apiUrl ? `${apiUrl}/v1/teams/${activeTeamId}` : `/v1/teams/${activeTeamId}`
        const teamResponse = await fetchWithAuth(
          teamUrl,
          { method: 'GET', headers: { Accept: 'application/json' } },
          undefined,
          { skipUnauthorizedRedirect: true }
        )
        if (!teamResponse.ok) {
          if (!cancelled) setIsOwner(false)
          return
        }

        const teamPayload = (await teamResponse.json()) as TeamDetailResponse
        const owner = String(teamPayload.role || '').trim().toLowerCase() === 'owner'
        ownerRoleCache.set(activeTeamId, owner)
        if (!cancelled) setIsOwner(owner)
      } catch {
        if (!cancelled) setIsOwner(false)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    void load()

    return () => {
      cancelled = true
    }
  }, [activeTeamId, hasSession])

  return useMemo(
    () => ({
      isOwner,
      isLoading,
      canManageBilling: !activeTeamId || !hasSession || isOwner,
    }),
    [activeTeamId, hasSession, isLoading, isOwner]
  )
}
